"use client"

import { Timestamp } from "firebase/firestore"
import type React from "react"
import { useState } from "react"
import { v4 as uuidv4 } from "uuid"
import dreamWorldProvider from "../../DreamWorldProvider"
import { storage } from "../../utils/firebase"
import { Event } from "../../types/events"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage"
import { useSelector } from "react-redux"
import { selectUserGlobalData } from "../../store/dreamworldSlice"
import BecomePartnerCard from "./BecomePartnerCard"

const EventGenerator: React.FC = () => {
  const userData = useSelector(selectUserGlobalData)
  const [title, setTitle] = useState("")
  const [subtitle, setSubtitle] = useState("")
  const [description, setDescription] = useState("")
  const [bannerImage, setBannerImage] = useState<File | null>(null)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [submissionDeadline, setSubmissionDeadline] = useState<Date | null>(null)
  const [winnerAnnouncement, setWinnerAnnouncement] = useState<Date | null>(null)
  const [timezone, setTimezone] = useState("UTC-8")
  const [requirements, setRequirements] = useState<string[]>([]);
  const [prizes, setPrizes] = useState([{ position: '1st Prize', winners: 1, reward: '', credits: 0 }]);
  const [evaluationDimensions, setEvaluationDimensions] = useState([{ name: '', description: '' }]);
  const [rules, setRules] = useState<string[]>([]);
  const [prizeGiving, setPrizeGiving] = useState<string[]>([]);
  const [howToParticipate, setHowToParticipate] = useState<string[]>([]);
  const [loading, setLoading] = useState(false)

  const handleCreateEvent = async () => {
    if (!title || !description || !startDate || !endDate || !bannerImage) {
      alert("Please fill all the required fields")
      return
    }

    setLoading(true)

    try {
      const imageRef = ref(storage, `eventImages/${uuidv4()}`);
      await uploadBytes(imageRef, bannerImage);
      const imageUrl = await getDownloadURL(imageRef);

      const id = uuidv4()
      const newEvent: Event = {
        creatorId:userData?.userData?.uid!,
        id,
        title,
        description,
        subtitle,
        bannerImage: imageUrl,
        startDate,
        endDate,
        submissionDeadline: submissionDeadline || endDate,
        winnerAnnouncement: winnerAnnouncement || endDate,
        timezone,
        requirements,
        prizes,
        evaluationDimensions,
        rules,
        prizeGiving,
        howToParticipate,
        status: 'upcoming',
      }
      await dreamWorldProvider.createEvent(newEvent)
      alert("Event created successfully!")

      setTitle("")
      setDescription("")
      setSubtitle("")
      setBannerImage(null)
      setStartDate(null)
      setEndDate(null)
      setSubmissionDeadline(null)
      setWinnerAnnouncement(null)
      setTimezone("UTC-8")
      setRequirements([])
      setPrizes([{ position: '1st Prize', winners: 1, reward: '', credits: 0 }])
      setEvaluationDimensions([{ name: '', description: '' }])
      setRules([])
      setPrizeGiving([])
      setHowToParticipate([])
    } catch (error) {
      console.error("Error creating event:", error)
      alert("Failed to create event")
    } finally {
      setLoading(false)
    }
  }

  const isPartner = userData?.userData?.isPartner
  const followers = userData?.userData?.followers ?? 0

  if (!isPartner || followers < 1000) {
    return <BecomePartnerCard />
  }

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-white mb-8 text-center lg:text-left">Create Event</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left Column - Basic Information */}
          <div className="space-y-6">
            <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-4">Basic Information</h3>

              <div className="space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-2">
                    Event Title
                  </label>
                  <input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition-colors"
                    placeholder="Enter event title"
                  />
                </div>
                <div>
                  <label htmlFor="subtitle" className="block text-sm font-medium text-gray-300 mb-2">
                    Subtitle
                  </label>
                  <input
                    id="subtitle"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition-colors"
                    placeholder="Enter event subtitle"
                  />
                </div>
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition-colors"
                    placeholder="Describe your event"
                  />
                </div>
              </div>
            </div>

            {/* Event Image Upload */}
            <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-4">Banner Image</h3>
              <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-indigo-500 transition-colors">
                <input
                  id="bannerImage"
                  type="file"
                  onChange={(e) => setBannerImage(e.target.files ? e.target.files[0] : null)}
                  className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-600 file:text-white hover:file:bg-indigo-700 file:cursor-pointer cursor-pointer"
                />
                <p className="text-gray-400 text-sm mt-2">Upload a banner image for your event</p>
              </div>
            </div>
          </div>

          {/* Right Column - Prizes, Dates, and Pricing */}
          <div className="space-y-6">
            {/* Prizes Section */}
            <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-4">Prizes</h3>
              <div className="space-y-4">
                {prizes.map((prize, index) => (
                  <div key={index} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <input
                      value={prize.position}
                      onChange={(e) => {
                        const newPrizes = [...prizes];
                        newPrizes[index].position = e.target.value;
                        setPrizes(newPrizes);
                      }}
                      className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition-colors"
                      placeholder="Position (e.g., 1st Prize)"
                    />
                    <input
                      type="number"
                      value={prize.winners}
                      onChange={(e) => {
                        const newPrizes = [...prizes];
                        newPrizes[index].winners = Number(e.target.value);
                        setPrizes(newPrizes);
                      }}
                      className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition-colors"
                      placeholder="Number of Winners"
                    />
                    <input
                      value={prize.reward}
                      onChange={(e) => {
                        const newPrizes = [...prizes];
                        newPrizes[index].reward = e.target.value;
                        setPrizes(newPrizes);
                      }}
                      className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition-colors"
                      placeholder="Reward description"
                    />
                  </div>
                ))}
                <button
                  onClick={() => setPrizes([...prizes, { position: '', winners: 1, reward: '', credits: 0 }])}
                  className="text-indigo-400 hover:text-indigo-300"
                >
                  + Add Prize
                </button>
              </div>
            </div>

            {/* Dates Section */}
            <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-4">Event Duration</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-300 mb-2">
                    Start Date
                  </label>
                  <input
                    id="startDate"
                    type="date"
                    value={startDate ? startDate.toISOString().split("T")[0] : ""}
                    onChange={(e) => setStartDate(new Date(e.target.value))}
                    className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-300 mb-2">
                    End Date
                  </label>
                  <input
                    id="endDate"
                    type="date"
                    value={endDate ? endDate.toISOString().split("T")[0] : ""}
                    onChange={(e) => setEndDate(new Date(e.target.value))}
                    className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition-colors"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <button
            onClick={handleCreateEvent}
            disabled={loading}
            className="w-full max-w-md px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:hover:scale-100 transition-all duration-200 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Creating Event...</span>
              </div>
            ) : (
              "Create Event"
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EventGenerator
